import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, CircularProgress, Stack, TextField } from '@mui/material';

import { translateBackendErrors } from '../../../../../../helpers/paymentHelper';
import { EmailRegEx } from '../../../../../../helpers/regularExpressions';
import {
  authorizePayment,
  SupportedBackendErrors,
} from '../../../../../../services/authorizePaymentService';
import { BankAccountType, PaymentType } from '../../../../../../services/commonTypes';
import SelectField from '../../../../../shared/SelectField';
import { IPaymentMethodPropsBase } from '../PaymentMethodsTypes';

import '../PaymentMethods.scss';

const Sdd = (props: IProps) => {
  const { t: translate } = useTranslation();

  const [errors, setErrors] = useState<IErrors>({});
  const [errorMessage, setErrorMessage] = useState<string | null | undefined>(props.errorMessage);
  const [isLoading, setIsLoading] = useState(false);

  const ibanRef = useRef<HTMLInputElement>(null);
  const bankTypeRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);

  const isFormValid = (): boolean => {
    const validationErrors: IErrors = {};

    if (!ibanRef.current?.value) {
      validationErrors.iban = translate('validation_errors.required');
    }

    if (!bankTypeRef.current?.value) {
      validationErrors.bankAccountType = translate('validation_errors.required');
    }

    if (!emailRef.current?.value) {
      validationErrors.email = translate('validation_errors.required');
    } else if (!EmailRegEx.test(emailRef.current.value)) {
      validationErrors.email = translate('validation_errors.invalid_email');
    }

    setErrors(validationErrors);

    return Object.keys(validationErrors).length === 0;
  };

  const handleSubmit = async (): Promise<void> => {
    const isValid = isFormValid();

    if (!isValid) {
      return;
    }

    setErrorMessage(null);
    setIsLoading(true);

    try {
      const authorizePaymentResult = await authorizePayment(
        props.authorizeLink,
        props.resourceToken,
        {
          bankAccountType:
            bankTypeRef.current?.value === 'business'
              ? BankAccountType.Business
              : BankAccountType.Consumer,
          email: emailRef.current?.value as string,
          iban: ibanRef.current?.value as string,
          type: PaymentType.Sdd,
        },
      );

      if ('errors' in authorizePaymentResult) {
        const authorizeErrors: IErrors = {};
        let authorizationErrorMessage = '';

        if (authorizePaymentResult.errors[SupportedBackendErrors.PaymentMethod]) {
          authorizationErrorMessage = translateBackendErrors(
            authorizePaymentResult.errors[SupportedBackendErrors.PaymentMethod],
            translate,
          );
        }
        if (authorizePaymentResult.errors[SupportedBackendErrors.Iban]) {
          const ibanErrorMessage = translateBackendErrors(
            authorizePaymentResult.errors[SupportedBackendErrors.Iban],
            translate,
          );
          authorizeErrors.iban = ibanErrorMessage;
        }
        if (authorizePaymentResult.errors[SupportedBackendErrors.Email]) {
          const emailErrorMessage = translateBackendErrors(
            authorizePaymentResult.errors[SupportedBackendErrors.Email],
            translate,
          );
          authorizeErrors.email = emailErrorMessage;
        }

        if (!authorizeErrors && !authorizationErrorMessage) {
          throw new Error(
            `Unknown error when validating the data: ${authorizePaymentResult.errors}`,
          );
        }

        setErrors(authorizeErrors);
        setErrorMessage(authorizationErrorMessage);
        setIsLoading(false);
      } else {
        if (
          authorizePaymentResult.workflow.next &&
          authorizePaymentResult.links[authorizePaymentResult.workflow.next]
        ) {
          const redirectionLink =
            authorizePaymentResult.links[authorizePaymentResult.workflow.next];
          window.location.href = redirectionLink.href;
        } else {
          throw new Error('Inconsistency at the payment workflow and links.');
        }
      }
    } catch (error) {
      console.error(error);
      setIsLoading(false);
      props.onUnhandledError(translate('errors.payment_generic'));
    }
  };

  useEffect(() => {
    props.onLoadingChange(isLoading);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  return (
    <div className="sdd-payment-container">
      <Stack spacing={2} className="payment-fields">
        {errorMessage && (
          <Alert severity="error" sx={{ marginBottom: '15px' }}>
            {errorMessage}
          </Alert>
        )}

        <TextField
          id="iban"
          inputRef={ibanRef}
          name="iban"
          label={translate('form_labels.iban')}
          error={Boolean(errors.iban)}
          helperText={errors.iban}
          disabled={isLoading}
          autoFocus
          required
        />

        <SelectField
          id="bankAccountType"
          name="bankAccountType"
          ref={bankTypeRef}
          label={translate('form_labels.bank_account_type')}
          disabled={isLoading}
          items={[
            {
              description: translate(`bank_account_types.${BankAccountType.Business}`),
              value: BankAccountType.Business,
            },
            {
              description: translate(`bank_account_types.${BankAccountType.Consumer}`),
              value: BankAccountType.Consumer,
            },
          ]}
          errorMessage={errors.bankAccountType}
          required
        />

        <TextField
          id="email"
          inputRef={emailRef}
          name="email"
          label={translate('form_labels.email')}
          error={Boolean(errors.email)}
          helperText={errors.email}
          disabled={isLoading}
          required
        />
      </Stack>

      <Button
        id="completePaymentBtn"
        className="complete-payment-btn"
        variant="contained"
        color="primary"
        size="large"
        type="submit"
        onClick={handleSubmit}
        disabled={isLoading}
      >
        {isLoading ? (
          <span style={{ marginRight: 10 }}>{translate('buttons.processing_payment')}</span>
        ) : (
          translate('buttons.complete_payment')
        )}
        {isLoading && <CircularProgress size={17} color="inherit" />}
      </Button>
    </div>
  );
};

export default Sdd;

type IProps = IPaymentMethodPropsBase;

interface IErrors {
  iban?: string | null;
  email?: string | null;
  bankAccountType?: string | null;
}
